import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@patternfly/react-icons';
import { AdminDriverDetail, acceptRate } from '@app/Drivers/types';
import { getDriver } from '@app/Drivers/drivers.api';
import { getDriverSchedule } from '@app/Schedules/schedules.api';
import { DaySchedule } from '@app/Schedules/types';
import { WeekScheduleDetail } from '@app/Schedules/WeekGrid';
import { RunStateBadge, ApprovalBadge, Pill } from '@app/Campaigns/badges';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '11px 14px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13.5,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
};

const km = (m: number | null) => (m == null ? '—' : `${(m / 1000).toFixed(1)} km`);
const pct = (p: number | null) => (p == null ? '—' : `${Math.round(p)}%`);

export const DriverDetailPage: React.FunctionComponent = () => {
  const { driverId } = useParams();
  const id = Number(driverId);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<AdminDriverDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<DaySchedule[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDriver(id)
      .then(d => {
        if (!cancelled) setDetail(d);
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Failed to load driver');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setSchedule(null);
    getDriverSchedule(id)
      .then(s => {
        if (!cancelled) setSchedule(s.schedules);
      })
      .catch(() => {
        if (!cancelled) setSchedule(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: colors.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 16 },
    headerRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 6 },
    title: { fontSize: 24, fontWeight: 800, color: colors.title, margin: 0 },
    sub: { fontSize: 14, color: colors.subtle, marginBottom: 20 },
    link: { color: colors.accent, cursor: 'pointer', fontWeight: 600 },
    statRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 },
    stat: { flex: '1 1 130px', background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, padding: '14px 16px' },
    statLabel: { fontSize: 12, fontWeight: 600, color: colors.subtle, textTransform: 'uppercase', letterSpacing: '0.3px' },
    statValue: { fontSize: 22, fontWeight: 800, color: colors.title, marginTop: 4 },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: colors.title, margin: '0 0 12px' }
  };

  const stat = (label: string, value: React.ReactNode) => (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.wrap}>
        <button style={styles.back} onClick={() => navigate('/drivers')}>
          <ArrowLeftIcon /> Back to Drivers
        </button>
        <div style={{ ...styles.card, padding: 48, textAlign: 'center', color: colors.subtle }}>Loading driver…</div>
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div style={styles.wrap}>
        <button style={styles.back} onClick={() => navigate('/drivers')}>
          <ArrowLeftIcon /> Back to Drivers
        </button>
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 12, padding: 20 }}>{error || 'Not found'}</div>
      </div>
    );
  }

  const d = detail.summary;
  const runs = detail.recentRuns;
  const rate = acceptRate(d);

  return (
    <div style={styles.wrap}>
      <button style={styles.back} onClick={() => navigate('/drivers')}>
        <ArrowLeftIcon /> Back to Drivers
      </button>

      <div style={styles.headerRow}>
        <h1 style={styles.title}>{d.name || `Driver #${d.driverProfileId}`}</h1>
        {d.available ? <Pill text="AVAILABLE" tone="green" /> : <Pill text="UNAVAILABLE" tone="gray" />}
      </div>
      <div style={styles.sub}>
        {d.email} · driver #{d.driverProfileId}
        {' · '}
        {d.pairedScreenId != null ? (
          <span style={styles.link} onClick={() => navigate(`/screens/${d.pairedScreenId}`)}>
            paired: {d.pairedScreenName || `screen #${d.pairedScreenId}`}
          </span>
        ) : (
          <span style={{ color: colors.placeholder }}>no paired screen</span>
        )}
      </div>

      <div style={styles.statRow}>
        {stat('Total runs', d.runsTotal)}
        {stat('Completed', d.runsCompleted)}
        {stat('In progress', d.runsInProgress)}
        {stat('Accept rate', rate == null ? '—' : `${rate}%`)}
        {stat('Declined', d.runsDeclined)}
        {stat('Distance', km(d.distanceMeters))}
      </div>

      <h2 style={styles.sectionTitle}>Weekly schedule</h2>
      <div style={{ ...styles.card, padding: 16, marginBottom: 22 }}>
        {schedule ? (
          <WeekScheduleDetail schedules={schedule} />
        ) : (
          <div style={{ padding: 24, textAlign: 'center', color: colors.subtle }}>Loading schedule…</div>
        )}
      </div>

      <h2 style={styles.sectionTitle}>Recent runs ({runs.length})</h2>
      <div style={styles.card}>
        {runs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.subtle }}>No runs for this driver yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
              <thead>
                <tr>
                  <th style={th}>Campaign</th>
                  <th style={th}>Slot start</th>
                  <th style={th}>State</th>
                  <th style={th}>Approval</th>
                  <th style={th}>Progress</th>
                  <th style={th}>Distance</th>
                  <th style={th}>Booked</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr
                    key={r.runId}
                    onClick={() => r.campaignId != null && navigate(`/campaigns/${r.campaignId}`)}
                    style={{ cursor: r.campaignId != null ? 'pointer' : 'default' }}
                  >
                    <td style={td}>{r.campaignName || `Campaign #${r.campaignId ?? '?'}`}</td>
                    <td style={td} title={formatDateTime(r.scheduledStartAt)}>{formatDateTime(r.scheduledStartAt)}</td>
                    <td style={td}>
                      <RunStateBadge state={r.state} />
                    </td>
                    <td style={td}>
                      <ApprovalBadge state={r.approvalState} />
                    </td>
                    <td style={td}>{pct(r.progressPercentage)}</td>
                    <td style={td}>{km(r.distanceTraveledMeters)}</td>
                    <td style={td}>{relativeTime(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
